"use client";

import { useState, useEffect, useCallback } from "react";
import { ontologyGetDashboards } from "../sdk";
import type { DashboardModel } from "../sdk";

// Re-export SDK types for consumers
export type { DashboardModel };

interface UseDashboardsResult {
  dashboards: DashboardModel[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing dashboards
 * @param orgId - Organization ID
 * @returns Dashboards management state and operations
 */
export function useDashboards(orgId: string): UseDashboardsResult {
  const [dashboards, setDashboards] = useState<DashboardModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboards = useCallback(async () => {
    if (!orgId) {
      setDashboards([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await ontologyGetDashboards({
        query: {
          org_id: orgId,
        },
      });
      setDashboards(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch dashboards");
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const refetch = useCallback(async () => {
    await fetchDashboards();
  }, [fetchDashboards]);

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  return {
    dashboards,
    loading,
    error,
    refetch,
  };
}
